/**
 * <cba-newsletter> — the footer's email signup (plan 6.1d: was the footer's
 * inline script). It subscribes through the shared @theme/klaviyo module
 * (assets/cba-klaviyo.js): email marketing consent on the list, which starts
 * the welcome flow. An ES module (DESIGN-PLAN 7.G6).
 *
 *   <cba-newsletter data-list-id="TaWNDK" data-success="Thanks…"
 *     data-email-missing="…" data-email-invalid="…" data-error="…">
 *     <input type="email" data-cba-newsletter-email>
 *     <button type="button" data-cba-newsletter-submit>Subscribe</button>
 *     <p id="…" role="status" hidden data-cba-newsletter-msg></p>
 *   </cba-newsletter>
 *
 * - No <form>: sending can never reload the page. The button and Enter send.
 * - An error stays until the address changes; the field is marked invalid and
 *   points at the message (WCAG 3.3.1, 1.3.1; brand/identity/05 Inputs).
 *   A thank-you fades after 5 s.
 */
import { subscribe, track, validEmail } from '@theme/klaviyo';

if (!customElements.get('cba-newsletter')) {
  class CbaNewsletter extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-cba-newsletter-email]');
      this.button = this.querySelector('[data-cba-newsletter-submit]');
      this.msg = this.querySelector('[data-cba-newsletter-msg]');
      if (!this.input || !this.button) return;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.input.addEventListener('input', () => this.clearError(), { signal });
      this.button.addEventListener('click', () => this.subscribe(), { signal });
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); this.subscribe(); }
      }, { signal });
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      clearTimeout(this.hideTimer);
    }

    show(text, isError) {
      if (!this.msg) return;
      this.msg.textContent = text;
      this.msg.classList.toggle('cba-field-error', isError);
      this.msg.classList.toggle('cba-footer__form-message--success', !isError);
      this.msg.hidden = false;
      clearTimeout(this.hideTimer);
      if (isError) {
        this.input.setAttribute('aria-invalid', 'true');
        this.input.setAttribute('aria-describedby', this.msg.id);
      } else {
        this.clearError();
        this.hideTimer = setTimeout(() => { this.msg.hidden = true; }, 5000);
      }
    }

    clearError() {
      this.input.removeAttribute('aria-invalid');
      this.input.removeAttribute('aria-describedby');
      if (this.msg && this.msg.classList.contains('cba-field-error')) this.msg.hidden = true;
    }

    subscribe() {
      const email = (this.input.value || '').trim();
      if (!email) { this.show(this.dataset.emailMissing, true); return; }
      if (!validEmail(email)) { this.show(this.dataset.emailInvalid, true); return; }
      this.button.disabled = true;
      subscribe(email, { listId: this.dataset.listId, source: 'Footer signup' })
        .then((res) => {
          this.button.disabled = false;
          if (!res.ok) { this.show(this.dataset.error, true); return; }
          this.input.value = '';
          this.show(this.dataset.success, false);
          track('Footer Newsletter Signup');
        })
        .catch(() => {
          this.button.disabled = false;
          this.show(this.dataset.error, true);
        });
    }
  }

  customElements.define('cba-newsletter', CbaNewsletter);
}
